/**
 * `PerformanceKpiCards` — Task 157.
 *
 * The 13 campaign-level KPI tiles for the performance summary. Pure
 * presentation: it receives already-aggregated, null-safe KPIs from the
 * `get-paid-media-performance-summary` EF and renders them in a responsive
 * `.card` grid (mirrors the panel's original `KpiTile`/`KpiStrip` look).
 *
 * Formatting is shared with `CampaignMetricsPanel` so money/count/ratio
 * rendering stays identical across the analytics surface and the raw daily
 * table. `null` ratios/values render as an em-dash.
 */

import type { PaidMediaPerformanceKpis } from '../../api/types';
import { fmtCount, fmtMoney, fmtPercent, fmtRoas } from './CampaignMetricsPanel';

interface PerformanceKpiCardsProps {
  readonly kpis: PaidMediaPerformanceKpis;
  readonly currencyCode: string | null;
}

export function PerformanceKpiCards({ kpis, currencyCode }: PerformanceKpiCardsProps) {
  const cards: ReadonlyArray<{ readonly label: string; readonly value: string }> = [
    { label: 'الإنفاق', value: fmtMoney(kpis.spend, currencyCode) },
    { label: 'الظهور', value: fmtCount(kpis.impressions) },
    { label: 'الوصول', value: fmtCount(kpis.reach) },
    { label: 'النقرات', value: fmtCount(kpis.clicks) },
    { label: 'نقرات الرابط', value: fmtCount(kpis.link_clicks) },
    { label: 'التحويلات', value: fmtCount(kpis.conversions) },
    { label: 'قيمة التحويل', value: fmtMoney(kpis.conversion_value, currencyCode) },
    { label: 'CTR', value: fmtPercent(kpis.ctr) },
    { label: 'CPC', value: fmtMoney(kpis.cpc, currencyCode) },
    { label: 'CPM', value: fmtMoney(kpis.cpm, currencyCode) },
    { label: 'CPA', value: fmtMoney(kpis.cpa, currencyCode) },
    { label: 'ROAS', value: fmtRoas(kpis.roas) },
    { label: 'CVR', value: fmtPercent(kpis.cvr) },
  ];

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit,minmax(130px,1fr))',
        gap: 12,
      }}
    >
      {cards.map((c) => (
        <div key={c.label} className="card" style={{ padding: 14 }}>
          <div className="lbl">{c.label}</div>
          <div className="body num" style={{ fontSize: 18, marginTop: 4, color: 'var(--ink)' }}>
            {c.value}
          </div>
        </div>
      ))}
    </div>
  );
}
